// InsertCitationDialog — single-select My Library picker with optional
// locator (page / paragraph / timestamp). Resolves the active piece's
// citation style, upserts the reference as `cited=true`, and returns the
// rendered citation parts so the caller can place it at the cursor.
//
// Behavior by style:
//   • chicago-nb → caller inserts a footnoteRef whose body is the rendered
//     footnote HTML (sanitized through the footnote-body whitelist).
//   • mla / apa  → caller inserts the in-text parenthetical text at cursor.
//
// We never silently flip an existing reference's `cited` flag without telling
// the user — but a Cite action on a previously "consulted" reference does
// imply the user wants it cited, so we flip and toast.
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  createBriefReferences,
  updateBriefReferenceCited,
} from "@/lib/composer/core/pieces/references";
import { toCslJson, type CslItem } from "@/lib/citations";
import {
  DEFAULT_CITATION_STYLE,
  getCitationStyleLabel,
  isNoteStyleHeuristic,
  renderCitation,
  resolveCitationStyle,
  type CitationStyle,
} from "@/lib/citationStyles";
import type { LibraryItem } from "@/lib/library";
import { readBriefCitationStyleOverride } from "@/lib/composer/core/pieces/briefs";

const STYLE_ID_RE = /^[a-z0-9][a-z0-9-]{0,127}$/i;
const isCitationStyle = (v: string | null | undefined): v is CitationStyle =>
  !!v && STYLE_ID_RE.test(v);

const firstAuthor = (item: { authors: LibraryItem["authors"]; author_text: LibraryItem["author_text"] }): string => {
  if (Array.isArray(item.authors) && item.authors.length > 0) return String(item.authors[0]);
  if (item.author_text) return item.author_text.split(/,| and /i)[0]?.trim() ?? "";
  return "";
};

const authorLastFirst = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const space = trimmed.lastIndexOf(" ");
  if (space === -1) return trimmed;
  return `${trimmed.slice(space + 1)}, ${trimmed.slice(0, space)}`;
};

export type InsertedCitation = {
  style: CitationStyle;
  /** "footnote" (chicago-nb) or "inline" (mla/apa) — drives caller placement. */
  placement: "footnote" | "inline";
  /** Rendered footnote body HTML (footnote placement) — already whitelist-safe. */
  footnoteBodyHtml: string;
  /** Rendered in-text parenthetical (inline placement). */
  inlineText: string;
  /** library_items.id this citation references — set on the footnoteRef node. */
  refId: string;
  /** Locator (e.g. "p. 42") if entered. */
  locator: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string | null;
  pieceId: string | null;
  onInsert: (result: InsertedCitation) => void;
};

export const InsertCitationDialog = ({
  open, onOpenChange, workspaceId, pieceId, onInsert,
}: Props) => {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [locator, setLocator] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resolvedStyle, setResolvedStyle] = useState<CitationStyle>(DEFAULT_CITATION_STYLE);

  // Resolve style: per-piece override → workspace default → chicago-nb.
  useEffect(() => {
    if (!open || !workspaceId || !pieceId) return;
    let cancelled = false;
    void (async () => {
      const [{ data: profile }, { data: piece }] = await Promise.all([
        supabase.from("profiles").select("default_citation_style").eq("id", workspaceId).maybeSingle(),
        supabase.from("content_pieces").select("metadata").eq("id", pieceId).maybeSingle(),
      ]);
      if (cancelled) return;
      const ws = (profile as { default_citation_style?: string | null } | null)?.default_citation_style ?? null;
      const override = readBriefCitationStyleOverride(
        (piece as { metadata?: unknown } | null)?.metadata as never,
      );
      setResolvedStyle(resolveCitationStyle(ws, override));
    })();
    return () => { cancelled = true; };
  }, [open, workspaceId, pieceId]);

  useEffect(() => {
    if (!open || !workspaceId) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from("library_items")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("title", { ascending: true })
        .limit(500);
      if (cancelled) return;
      if (error) toast.error("Failed to load My Library");
      else setItems((data ?? []) as LibraryItem[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, workspaceId]);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setLocator("");
      setQuery("");
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => {
      const auths = Array.isArray(i.authors) ? (i.authors as unknown[]).map(String) : [];
      return [i.title, i.author_text ?? "", ...auths]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [items, query]);

  const selectedItem = useMemo(
    () => (selectedId ? items.find((i) => i.id === selectedId) ?? null : null),
    [items, selectedId],
  );

  const handleInsert = async () => {
    if (!workspaceId || !pieceId || !selectedItem) return;
    setBusy(true);
    try {
      const author = authorLastFirst(firstAuthor(selectedItem));
      const year = selectedItem.published_year ? ` (${selectedItem.published_year})` : "";
      const label = author
        ? `${author}${year}. ${selectedItem.title}`
        : `${selectedItem.title}${year}`;

      // Upsert reference as cited=true.
      await createBriefReferences(workspaceId, pieceId, [
        {
          referenceType: "source" as const,
          referenceId: selectedItem.id,
          labelSnapshot: label,
          sortOrder: 0,
          cited: true,
        },
      ] as never);

      // If a row already existed and was set to "consulted", flip it to cited.
      const { data: existing } = await supabase
        .from("content_piece_references")
        .select("id, cited")
        .eq("workspace_id", workspaceId)
        .eq("piece_id", pieceId)
        .eq("reference_type", "source")
        .eq("reference_id", selectedItem.id)
        .maybeSingle();
      const row = existing as { id: string; cited: boolean | null } | null;
      let flipped = false;
      if (row && row.cited === false) {
        await updateBriefReferenceCited(workspaceId, row.id, true);
        flipped = true;
      }

      const csl: CslItem = toCslJson(selectedItem);
      const trimmedLoc = locator.trim() || null;

      const rendered = await renderCitation(csl, {
        style: resolvedStyle,
        mode: "footnote",
        locator: trimmedLoc,
      });
      const result: InsertedCitation = {
        style: resolvedStyle,
        placement: isNoteStyleHeuristic(resolvedStyle) ? "footnote" : "inline",
        footnoteBodyHtml: rendered.html,
        inlineText: rendered.text,
        refId: selectedItem.id,
        locator: trimmedLoc,
      };

      onInsert(result);
      if (flipped) toast.success("Reference moved from Consulted to Cited");
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Insert citation</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            Style: <span className="font-medium text-foreground">{getCitationStyleLabel(resolvedStyle)}</span>
            {" "}— change in the Bibliography tab.
          </div>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search My Library…"
            className="h-9"
          />
          <ScrollArea className="h-56 rounded-md border">
            {loading ? (
              <div className="p-4 text-sm text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">
                {items.length === 0 ? "Your My Library is empty." : "No matches."}
              </div>
            ) : (
              <ul className="divide-y">
                {filtered.map((item) => {
                  const isSel = selectedId === item.id;
                  const author = authorLastFirst(firstAuthor(item));
                  return (
                    <li
                      key={item.id}
                      onClick={() => setSelectedId(item.id)}
                      className={`cursor-pointer px-3 py-2 hover:bg-muted/50 ${isSel ? "bg-primary/10" : ""}`}
                    >
                      <div className="truncate text-sm font-medium">{item.title}</div>
                      {author && <div className="truncate text-xs text-muted-foreground">{author}</div>}
                    </li>
                  );
                })}
              </ul>
            )}
          </ScrollArea>
          <div className="space-y-1">
            <Label htmlFor="cite-locator" className="text-xs">
              Locator (optional)
            </Label>
            <Input
              id="cite-locator"
              value={locator}
              onChange={(e) => setLocator(e.target.value)}
              placeholder='e.g. "p. 42", "ch. 3", "12:34"'
              className="h-9"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleInsert} disabled={busy || !selectedItem || !pieceId}>
            {busy ? "Inserting…" : "Insert citation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
